import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  IconButton,
  TextField,
  Chip,
  Tooltip,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ToggleButton,
  ToggleButtonGroup,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { useAuth } from '../hooks/useAuth';
import {
  getUserCustomSigns,
  saveCustomSign,
  deleteCustomSign,
  toggleCustomSignFavorite,
  CustomCompositeSign,
} from '../services/customSigns';

export interface SignComponent {
  id: string;
  signCode: string;
  x: number;           // Position in mm
  y: number;
  width: number;       // Dimensions in mm
  height: number;
  svg: string;
  slotIndex: number;   // Which slot this sign is in
}

export interface CompositeSign {
  id: string;
  name: string;
  components: SignComponent[];
  canvasWidth: number;
  canvasHeight: number;
}

const CATEGORY_ORDER = [
  '🛑 Regulatory & Control',
  '🚧 Work Zone & Temporary',
  '⚠️ Warning & Advisory',
  '➡️ Directional & Guide',
];

interface CompositeSignBuilderProps {
  open: boolean;
  onClose: () => void;
  onSave: (composite: CompositeSign) => void;
  signLibrary: Record<string, any> | null;
}

interface Slot {
  x: number;
  y: number;
  width: number;
  height: number;
  label: string;
}

// Fixed slots for multi-message boards
const SLOTS: Slot[] = [
  { x: 0, y: 0, width: 600, height: 600, label: 'Top Left (600×600mm)' },
  { x: 600, y: 0, width: 600, height: 600, label: 'Top Right (600×600mm)' },
  { x: 0, y: 600, width: 1200, height: 300, label: 'Bottom (1200×300mm)' },
];

const CANVAS_WIDTH = 1200;
const CANVAS_HEIGHT = 900;

export const CompositeSignBuilder: React.FC<CompositeSignBuilderProps> = ({
  open,
  onClose,
  onSave,
  signLibrary,
}) => {
  const { user, getToken } = useAuth();
  
  // Component state
  const [compositeName, setCompositeName] = useState('Multi-Message Board');
  const [components, setComponents] = useState<SignComponent[]>([]);
  const [zoom, setZoom] = useState(0.5);
  const [searchQuery, setSearchQuery] = useState('');
  const [dragOverSlot, setDragOverSlot] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<number>(0); // 0 = Signs Library, 1 = My Custom Signs
  
  // Custom signs state
  const [customSigns, setCustomSigns] = useState<CustomCompositeSign[]>([]);
  const [loadingCustomSigns, setLoadingCustomSigns] = useState(false);
  const [customSignsError, setCustomSignsError] = useState<string | null>(null);
  
  const canvasRef = useRef<HTMLDivElement>(null);

  // Load user's custom signs when dialog opens
  useEffect(() => {
    if (open && user) {
      loadCustomSigns();
    }
  }, [open, user]);

  const loadCustomSigns = async () => {
    try {
      setLoadingCustomSigns(true);
      setCustomSignsError(null);
      const token = await getToken();
      if (token) {
        const signs = await getUserCustomSigns(token);
        setCustomSigns(signs);
      }
    } catch (error) {
      console.error('Error loading custom signs:', error);
      setCustomSignsError('Failed to load custom signs');
    } finally {
      setLoadingCustomSigns(false);
    }
  };

  // Define category order for consistent display (same as sign library)
  const CATEGORY_ORDER = [
    '🛑 Regulatory & Control',
    '🚧 Work Zone & Temporary',
    '⚠️ Warning & Advisory',
    '➡️ Directional & Guide',
  ];

  // Filter and group signs from library by subcategory
  const { signsBySubcategory, totalCount } = React.useMemo(() => {
    if (!signLibrary) return { signsBySubcategory: {}, totalCount: 0 };
    
    const filtered = Object.entries(signLibrary)
      .map(([code, data]: [string, any]) => ({
        code,
        svg: data.svg || '',
        description: data.description || code,
        category: data.category || 'General',
        subcategory: data.subcategory || 'Other',
        subcategoryDescription: data.subcategory_description || data.subcategory || 'Other',
        states: data.states || [],
      }))
      .filter(sign => {
        // Filter by search query
        const matchesSearch = sign.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
          sign.description.toLowerCase().includes(searchQuery.toLowerCase());
        
        return matchesSearch;
      });
    
    // Group by subcategory
    const grouped: Record<string, any[]> = {};
    filtered.forEach(sign => {
      const subcat = sign.subcategory;
      if (!grouped[subcat]) {
        grouped[subcat] = [];
      }
      grouped[subcat].push(sign);
    });
    
    // Sort each group by code
    Object.keys(grouped).forEach(subcat => {
      grouped[subcat].sort((a, b) => a.code.localeCompare(b.code));
    });
    
    return { 
      signsBySubcategory: grouped, 
      totalCount: filtered.length 
    };
  }, [signLibrary, searchQuery]);

  // Track expanded subcategories (default to first 3 expanded)
  const [expandedSubcategories, setExpandedSubcategories] = useState<Set<string>>(
    new Set(Object.keys(signsBySubcategory).slice(0, 3))
  );

  const toggleSubcategory = (subcategory: string) => {
    setExpandedSubcategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(subcategory)) {
        newSet.delete(subcategory);
      } else {
        newSet.add(subcategory);
      }
      return newSet;
    });
  };

  // Find which slot a point is in
  const findSlotAtPosition = (x: number, y: number): number => {
    for (let i = 0; i < SLOTS.length; i++) {
      const slot = SLOTS[i];
      if (
        x >= slot.x &&
        x < slot.x + slot.width &&
        y >= slot.y &&
        y < slot.y + slot.height
      ) {
        return i;
      }
    }
    return -1;
  };

  // Handle drag start from library
  const handleDragStartFromLibrary = (e: React.DragEvent, signCode: string, svg: string) => {
    const dragData = JSON.stringify({ signCode, svg, fromLibrary: true });
    e.dataTransfer.setData('application/json', dragData);
    e.dataTransfer.effectAllowed = 'copy';
  };

  // Handle drop on canvas
  const handleCanvasDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOverSlot(null);
    
    const data = e.dataTransfer.getData('application/json');
    if (!data) return;
    
    try {
      const { signCode, svg, fromLibrary } = JSON.parse(data);
      
      if (fromLibrary) {
        const rect = canvasRef.current?.getBoundingClientRect();
        if (!rect) return;
        
        // Calculate position in mm (account for zoom)
        const rawX = (e.clientX - rect.left) / zoom;
        const rawY = (e.clientY - rect.top) / zoom;
        
        // Find which slot this dropped into
        const slotIndex = findSlotAtPosition(rawX, rawY);
        
        if (slotIndex === -1) {
          return;
        }

        const slot = SLOTS[slotIndex];

        // Check if slot is already occupied
        const slotOccupied = components.some(c => c.slotIndex === slotIndex);
        if (slotOccupied) {
          alert(`This slot is already occupied. Remove the existing sign first.`);
          return;
        }

        const newComponent: SignComponent = {
          id: `component-${Date.now()}`,
          signCode,
          x: slot.x,
          y: slot.y,
          width: slot.width,
          height: slot.height,
          svg,
          slotIndex,
        };
        
        setComponents(prev => [...prev, newComponent]);
      }
    } catch (error) {
      console.error('Drop error:', error);
    }
  };

  const handleCanvasDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';

    // Highlight slot being hovered over
    const rect = canvasRef.current?.getBoundingClientRect();
    if (rect) {
      const rawX = (e.clientX - rect.left) / zoom;
      const rawY = (e.clientY - rect.top) / zoom;
      const slotIndex = findSlotAtPosition(rawX, rawY);
      setDragOverSlot(slotIndex);
    }
  };

  const handleCanvasDragLeave = () => {
    setDragOverSlot(null);
  };

  // Remove component
  const removeComponent = (id: string) => {
    setComponents(prev => prev.filter(c => c.id !== id));
  };

  // Handle save
  const handleSave = () => {
    if (components.length === 0) {
      alert('Please add at least one sign to the board');
      return;
    }

    const composite: CompositeSign = {
      id: `composite-${Date.now()}`,
      name: compositeName,
      components,
      canvasWidth: CANVAS_WIDTH,
      canvasHeight: CANVAS_HEIGHT,
    };
    
    onSave(composite);
    
    // Reset form
    setCompositeName('Multi-Message Board');
    setComponents([]);
  };

  // Save current board as a custom sign
  const handleSaveAsCustomSign = async () => {
    if (!user) {
      alert('Please sign in to save custom signs');
      return;
    }

    if (components.length !== SLOTS.length) {
      alert('Please fill all slots before saving');
      return;
    }

    try {
      const token = await getToken();
      if (!token) {
        alert('Authentication required');
        return;
      }

      await saveCustomSign(token, {
        name: compositeName,
        components,
        canvasWidth: CANVAS_WIDTH,
        canvasHeight: CANVAS_HEIGHT,
      });

      alert('Custom sign saved successfully!');
      await loadCustomSigns(); // Reload custom signs list
    } catch (error) {
      console.error('Error saving custom sign:', error);
      alert('Failed to save custom sign');
    }
  };

  // Delete a custom sign
  const handleDeleteCustomSign = async (signId: string) => {
    if (!window.confirm('Are you sure you want to delete this custom sign?')) {
      return;
    }

    try {
      const token = await getToken();
      if (!token) {
        alert('Authentication required');
        return;
      }

      await deleteCustomSign(token, signId);
      await loadCustomSigns(); // Reload list
    } catch (error) {
      console.error('Error deleting custom sign:', error);
      alert('Failed to delete custom sign');
    }
  };

  // Load a custom sign onto the canvas
  const handleLoadCustomSign = (customSign: CustomCompositeSign) => {
    setCompositeName(customSign.name);
    setComponents(customSign.components);
    setActiveTab(0); // Switch to sign library tab
  };

  // Toggle favorite status
  const handleToggleFavorite = async (signId: string) => {
    try {
      const token = await getToken();
      if (!token) return;

      await toggleCustomSignFavorite(token, signId);
      await loadCustomSigns(); // Reload to update UI
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  // SVG to data URL helper
  const svgToDataUrl = (svg: string) => {
    try {
      const base64 = window.btoa(unescape(encodeURIComponent(svg)));
      return `data:image/svg+xml;base64,${base64}`;
    } catch (e) {
      return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
    }
  };

  const getSlotFillColor = (slotIndex: number) => {
    const hasSign = components.some(c => c.slotIndex === slotIndex);
    const isDraggingOver = dragOverSlot === slotIndex;
    
    if (hasSign) return 'transparent';
    if (isDraggingOver) return 'rgba(76, 175, 80, 0.15)'; // Green when hovering
    return 'rgba(33, 150, 243, 0.08)'; // Light blue empty
  };

  const getSlotBorderColor = (slotIndex: number) => {
    const hasSign = components.some(c => c.slotIndex === slotIndex);
    const isDraggingOver = dragOverSlot === slotIndex;
    
    if (isDraggingOver) return '#4CAF50';
    if (hasSign) return '#2196F3';
    return '#90CAF9';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: '90vw',
          height: '85vh',
          maxHeight: '85vh',
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box>
          <Typography variant="h5">Scratchpad</Typography>
          <Typography variant="caption" color="text.secondary">
            Drag signs into the slots to create your board
          </Typography>
        </Box>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', height: 'calc(100% - 140px)' }}>
        {/* Left Sidebar */}
        <Box
          sx={{
            width: 350,
            borderRight: '1px solid #e0e0e0',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Tabs for Sign Library vs My Custom Signs */}
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={{ borderBottom: '1px solid #e0e0e0' }}
          >
            <Tab label="Sign Library" />
            <Tab label="My Custom Signs" disabled={!user} />
          </Tabs>

          {/* Tab Panel 0: Sign Library */}
          {activeTab === 0 && (
            <>
              <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search signs..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  sx={{ mb: 1 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {totalCount} signs in {Object.keys(signsBySubcategory).length} categories
                </Typography>
              </Box>

          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {Object.entries(signsBySubcategory)
              .sort(([catA], [catB]) => {
                const indexA = CATEGORY_ORDER.indexOf(catA);
                const indexB = CATEGORY_ORDER.indexOf(catB);
                // If both are in the order array, sort by their index
                if (indexA !== -1 && indexB !== -1) return indexA - indexB;
                // If only one is in the array, prioritize it
                if (indexA !== -1) return -1;
                if (indexB !== -1) return 1;
                // Otherwise, sort alphabetically
                return catA.localeCompare(catB);
              })
              .map(([subcategory, signs]) => {
                const isExpanded = expandedSubcategories.has(subcategory);
                const subcatDesc = signs[0]?.subcategoryDescription || subcategory;
                
                return (
                  <Accordion
                    key={subcategory}
                    expanded={isExpanded}
                    onChange={() => toggleSubcategory(subcategory)}
                    disableGutters
                    elevation={0}
                    sx={{
                      '&:before': { display: 'none' },
                      borderBottom: '1px solid #e0e0e0',
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        minHeight: 48,
                        px: 2,
                        bgcolor: isExpanded ? '#f5f5f5' : 'transparent',
                        '&:hover': { bgcolor: '#f8f8f8' },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
                          {subcatDesc}
                        </Typography>
                        <Chip 
                          label={signs.length} 
                          size="small" 
                          sx={{ 
                            height: 20, 
                            fontSize: '0.7rem',
                            bgcolor: '#e3f2fd',
                            color: '#1976d2',
                          }} 
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 1.5, pt: 1 }}>
                      <Grid container spacing={1}>
                        {signs.map((sign: any) => (
                          <Grid item xs={6} key={sign.code}>
                            <Paper
                              draggable
                              onDragStart={(e) => handleDragStartFromLibrary(e, sign.code, sign.svg)}
                              sx={{
                                p: 1,
                                cursor: 'grab',
                                '&:active': { cursor: 'grabbing' },
                                '&:hover': { 
                                  bgcolor: '#f5f5f5', 
                                  transform: 'scale(1.05)',
                                  boxShadow: 2,
                                },
                                transition: 'all 0.2s',
                                border: '1px solid #e0e0e0',
                              }}
                            >
                              <Box
                                sx={{
                                  width: '100%',
                                  height: 70,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  mb: 0.5,
                                }}
                              >
                                <img
                                  src={svgToDataUrl(sign.svg)}
                                  alt={sign.code}
                                  style={{ maxWidth: '100%', maxHeight: '100%', pointerEvents: 'none' }}
                                />
                              </Box>
                              <Typography
                                variant="caption"
                                sx={{
                                  display: 'block',
                                  textAlign: 'center',
                                  fontSize: '0.65rem',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  fontWeight: 500,
                                }}
                              >
                                {sign.code.toUpperCase()}
                              </Typography>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
          </Box>
            </>
          )}

          {/* Tab Panel 1: My Custom Signs */}
          {activeTab === 1 && (
            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
              {loadingCustomSigns && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              )}

              {customSignsError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {customSignsError}
                </Alert>
              )}

              {!loadingCustomSigns && customSigns.length === 0 && (
                <Alert severity="info">
                  No custom signs saved yet. Create a board and click "Save to My Library" to save it!
                </Alert>
              )}

              {!loadingCustomSigns && customSigns.length > 0 && (
                <Grid container spacing={2}>
                  {customSigns.map(customSign => (
                    <Grid item xs={12} key={customSign.id}>
                      <Paper
                        sx={{
                          p: 2,
                          cursor: 'pointer',
                          '&:hover': {
                            bgcolor: '#f5f5f5',
                            boxShadow: 2,
                          },
                          transition: 'all 0.2s',
                          border: '1px solid #e0e0e0',
                        }}
                        onClick={() => handleLoadCustomSign(customSign)}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {customSign.name}
                          </Typography>
                          <Box>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (customSign.id) handleToggleFavorite(customSign.id);
                              }}
                            >
                              {customSign.is_favorite ? (
                                <StarIcon fontSize="small" color="warning" />
                              ) : (
                                <StarBorderIcon fontSize="small" />
                              )}
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (customSign.id) handleDeleteCustomSign(customSign.id);
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {customSign.components.length} signs • Used {customSign.usage_count || 0} times
                        </Typography>
                        {customSign.created_at && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Created {new Date(customSign.created_at).toLocaleDateString()}
                          </Typography>
                        )}
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          )}
        </Box>

        {/* Center - Canvas with Slots */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Canvas Toolbar */}
          <Box
            sx={{
              p: 1.5,
              borderBottom: '1px solid #e0e0e0',
              display: 'flex',
              gap: 2,
              alignItems: 'center',
              bgcolor: '#fafafa',
            }}
          >
            <TextField
              size="small"
              label="Board Name"
              value={compositeName}
              onChange={(e) => setCompositeName(e.target.value)}
              sx={{ width: 250 }}
            />
            <Chip
              label={`${CANVAS_WIDTH} × ${CANVAS_HEIGHT} mm`}
              size="small"
              color="primary"
              variant="outlined"
            />
            <Chip 
              label={`${components.length}/3 slots filled`} 
              size="small" 
              color={components.length === 3 ? 'success' : 'default'}
            />
          </Box>

          {/* Canvas Area */}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              p: 4,
              bgcolor: '#e8e8e8',
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              ref={canvasRef}
              onDrop={handleCanvasDrop}
              onDragOver={handleCanvasDragOver}
              onDragLeave={handleCanvasDragLeave}
              sx={{
                width: CANVAS_WIDTH * zoom,
                height: CANVAS_HEIGHT * zoom,
                bgcolor: 'white',
                border: '4px solid #333',
                boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                position: 'relative',
              }}
            >
              {/* Render placeholder slots */}
              {SLOTS.map((slot, index) => (
                <Box
                  key={index}
                  sx={{
                    position: 'absolute',
                    left: slot.x * zoom,
                    top: slot.y * zoom,
                    width: slot.width * zoom,
                    height: slot.height * zoom,
                    border: `3px dashed ${getSlotBorderColor(index)}`,
                    bgcolor: getSlotFillColor(index),
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pointerEvents: 'none',
                  }}
                >
                  {!components.some(c => c.slotIndex === index) && (
                    <Box sx={{ textAlign: 'center', p: 2 }}>
                      <Typography 
                        variant="h6" 
                        color="text.secondary"
                        sx={{ 
                          fontWeight: 'bold',
                          opacity: 0.5,
                          userSelect: 'none',
                        }}
                      >
                        {slot.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.5 }}>
                        Drop sign here
                      </Typography>
                    </Box>
                  )}
                </Box>
              ))}

              {/* Render placed signs */}
              {components.map((component) => (
                <Box
                  key={component.id}
                  sx={{
                    position: 'absolute',
                    left: component.x * zoom,
                    top: component.y * zoom,
                    width: component.width * zoom,
                    height: component.height * zoom,
                    border: '3px solid #2196F3',
                    bgcolor: 'white',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    '&:hover': {
                      boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
                      '& .delete-btn': {
                        opacity: 1,
                      },
                    },
                  }}
                >
                  <img
                    src={svgToDataUrl(component.svg)}
                    alt={component.signCode}
                    style={{
                      width: '100%',
                      height: '100%',
                      pointerEvents: 'none',
                    }}
                  />
                  <IconButton
                    className="delete-btn"
                    size="small"
                    onClick={() => removeComponent(component.id)}
                    sx={{
                      position: 'absolute',
                      top: -16,
                      right: -16,
                      bgcolor: 'error.main',
                      color: 'white',
                      opacity: 0,
                      transition: 'opacity 0.2s',
                      width: 32,
                      height: 32,
                      zIndex: 10,
                      '&:hover': {
                        bgcolor: 'error.dark',
                      },
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                  <Chip
                    label={component.signCode.toUpperCase()}
                    size="small"
                    sx={{
                      position: 'absolute',
                      bottom: 4,
                      left: 4,
                      fontSize: '0.7rem',
                      height: 20,
                      fontWeight: 'bold',
                    }}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {/* Right Sidebar - Instructions */}
        <Box
          sx={{
            width: 280,
            borderLeft: '1px solid #e0e0e0',
            p: 3,
            bgcolor: '#fafafa',
          }}
        >
          <Typography variant="h6" gutterBottom>
            Instructions
          </Typography>
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" paragraph>
              <strong>Step 1:</strong> Drag signs from the left library
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Step 2:</strong> Drop them into the 3 slots:
            </Typography>
            <Box sx={{ pl: 2, mb: 2 }}>
              {SLOTS.map((slot, i) => (
                <Chip
                  key={i}
                  label={slot.label}
                  size="small"
                  color={components.some(c => c.slotIndex === i) ? 'success' : 'default'}
                  sx={{ display: 'block', mb: 0.5, width: 'fit-content' }}
                  icon={components.some(c => c.slotIndex === i) ? 
                    <span>✓</span> : <span>{i + 1}</span>
                  }
                />
              ))}
            </Box>
            <Typography variant="body2" paragraph>
              <strong>Step 3:</strong> Name your board and click Save
            </Typography>
          </Box>

          <Paper sx={{ p: 2, bgcolor: '#fff3cd', border: '1px solid #ffeb3b' }}>
            <Typography variant="caption" display="block" gutterBottom fontWeight="bold">
              Tips:
            </Typography>
            <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
              • Signs auto-fit to slot size
            </Typography>
            <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
              • Hover to delete a sign
            </Typography>
            <Typography variant="caption" display="block">
              • Slots highlight when dragging
            </Typography>
          </Paper>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0', gap: 1, justifyContent: 'space-between' }}>
        <Box>
          {user && (
            <Button
              variant="outlined"
              startIcon={<StarIcon />}
              onClick={handleSaveAsCustomSign}
              disabled={components.length !== SLOTS.length}
              color="secondary"
            >
              Save to My Library
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} variant="outlined">
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={components.length === 0}
            size="large"
          >
            Use Board ({components.length}/3 signs)
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
